/* mega-header.js — <mega-header> and <mega-side-nav> (sections/mega-header.liquid).

   Migrated from the coterie theme's header.js and wired into mydryper's own
   contracts instead of coterie's:

   * chrome metrics — publishes --header-height (the pinned chrome: announcement
     bar + bar while sticky, 0px when not) and --header-bottom (the bar's live
     viewport bottom), exactly as the classic <header-nav> does, so
     scroll-padding, sticky cart summary, FAQ nav and the panels keep working;
   * sticky — mirrors the setting onto .header-group-sticky[data-sticky-off] and
     toggles `is-stuck` once the page scrolls (shadow + solid palette);
   * scroll lock — window.themeScrollLock from global.js (reference-counted, so
     it cooperates with the cart drawer and the age popup);
   * drawer API — `drawerOpen` / `setDrawer(open)`, the names cart.js and
     global.js already call on the header to close it under another modal.

   Two patterns recur:
   1. TWO-PHASE REVEAL. An element that fades or slides must be rendered for a
      frame before the class that animates it lands, so every open is
      `hidden = false` -> rAF -> add class, and every close is remove class ->
      wait out the transition -> `hidden = true`. Staying un-hidden while
      invisible would leave its links in the tab order and the a11y tree.
   2. PER-ELEMENT TIMERS. A pending hide lives on the element it hides, so
      opening panel B never strands the hide of panel A. */

const DESKTOP = window.matchMedia('(min-width: 990px)');
const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

/* Longest transition each close waits out, in ms, mirroring the CSS custom
   properties in mega-header.liquid (a few ms long is harmless). */
const PANEL_FADE_OUT = 300;
const SUBNAV_SLIDE = 500;
const DRAWER_SLIDE = 600;

const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])';

function nextFrame(fn) {
  if (reducedMotion.matches) fn();
  else window.requestAnimationFrame(fn);
}

function hideAfter(el, ms) {
  window.clearTimeout(el.megaHideTimer);
  if (reducedMotion.matches) {
    el.hidden = true;
    return;
  }
  el.megaHideTimer = window.setTimeout(() => {
    el.hidden = true;
  }, ms);
}

function scrollLock() {
  return (
    window.themeScrollLock || {
      lock: () => document.body.classList.add('drawer-lock'),
      unlock: () => document.body.classList.remove('drawer-lock'),
    }
  );
}

function isTabbable(el) {
  if (el.closest('[inert], [hidden]')) return false;
  if (!el.getClientRects().length) return false;
  return getComputedStyle(el).visibility !== 'hidden';
}

/* ------------------------------------------------------------------ header */

class MegaHeader extends HTMLElement {
  connectedCallback() {
    this.bar = this.querySelector('[data-header-bar]');
    this.items = Array.from(this.querySelectorAll('[data-mega-item]'));
    this.sideNav = this.querySelector('mega-side-nav');
    this.burger = this.querySelector('[data-side-nav-open]');
    this.openItem = null;

    this.stickyWrap = this.closest('.header-group-sticky');
    this.sticky = this.dataset.sticky === 'true';
    if (this.stickyWrap) this.stickyWrap.toggleAttribute('data-sticky-off', !this.sticky);

    this.initMetrics();
    this.initMegaMenus();
    this.initSideNav();
    this.initEditor();
  }

  disconnectedCallback() {
    if (this.resizeObserver) this.resizeObserver.disconnect();
    if (this.metricsFrame) window.cancelAnimationFrame(this.metricsFrame);
    window.removeEventListener('scroll', this.onScroll);
    window.removeEventListener('resize', this.onResize);
    document.removeEventListener('keydown', this.onEscape);
    document.removeEventListener('shopify:block:select', this.onBlockSelect);
    document.removeEventListener('shopify:block:deselect', this.onBlockDeselect);
    DESKTOP.removeEventListener('change', this.onBreakpoint);
    this.closeMenu();
  }

  /* --- chrome metrics ---------------------------------------------------- */

  initMetrics() {
    this.syncMetrics = () => {
      if (this.metricsFrame || !this.bar) return;
      this.metricsFrame = window.requestAnimationFrame(() => {
        this.metricsFrame = 0;
        const barRect = this.bar.getBoundingClientRect();
        /* The pinned chrome is the whole group down to the bar's bottom edge.
           Measured to the bar rather than from the group's own height because
           a transparent bar is out of flow and the group box ends above it. */
        let pinned = 0;
        if (this.sticky) {
          const top = this.stickyWrap ? this.stickyWrap.getBoundingClientRect().top : barRect.top;
          pinned = Math.max(0, barRect.bottom - top);
        }
        const root = document.documentElement.style;
        root.setProperty('--header-height', `${Math.round(pinned)}px`);
        root.setProperty('--header-bottom', `${Math.max(0, Math.round(barRect.bottom))}px`);
      });
    };

    if ('ResizeObserver' in window && this.bar) {
      this.resizeObserver = new ResizeObserver(this.syncMetrics);
      this.resizeObserver.observe(this.bar);
      if (this.stickyWrap) this.resizeObserver.observe(this.stickyWrap);
    }

    this.onScroll = () => {
      if (this.sticky) this.classList.toggle('is-stuck', window.scrollY > 0);
      this.syncMetrics();
    };
    this.onResize = () => this.syncMetrics();
    this.onScroll();
    window.addEventListener('scroll', this.onScroll, { passive: true });
    window.addEventListener('resize', this.onResize);
  }

  /* --- mega menus ---------------------------------------------------------
     Hover-driven on desktop pointers, click-driven otherwise (and for the
     keyboard at any width), because hover intent does not exist on touch. */

  initMegaMenus() {
    this.items.forEach((item) => {
      const trigger = item.querySelector('.mega-header__link');
      if (!trigger) return;

      /* Hover intent from a real mouse only. A touch screen at desktop width
         (a tablet in landscape) fires emulated pointer events too, and
         opening on those would make the first tap navigate instead of
         revealing the panel. */
      item.addEventListener('pointerenter', (event) => {
        if (!DESKTOP.matches || event.pointerType !== 'mouse') return;
        window.clearTimeout(this.closeTimer);
        this.openMenu(item);
      });
      item.addEventListener('pointerleave', (event) => {
        if (!DESKTOP.matches || event.pointerType !== 'mouse') return;
        /* A short grace period so the pointer can cross into the panel. The
           keyboard may be inside the panel, and hiding it would blur the link
           being read, so a focused panel stays. */
        this.closeTimer = window.setTimeout(() => {
          if (this.openItem && this.openItem.contains(document.activeElement)) return;
          this.closeMenu();
        }, 120);
      });

      /* The first activation (tap, Enter) opens the panel; with it open, the
         trigger is a plain link again, so the panel is an enhancement rather
         than a trap. */
      trigger.addEventListener('click', (event) => {
        if (this.openItem === item) return;
        event.preventDefault();
        this.openMenu(item);
      });
    });

    this.onEscape = (event) => {
      if (event.key !== 'Escape' || !this.openItem) return;
      const trigger = this.openItem.querySelector('.mega-header__link');
      this.closeMenu();
      if (trigger) trigger.focus();
    };
    document.addEventListener('keydown', this.onEscape);

    this.addEventListener('focusout', (event) => {
      if (!this.openItem || !event.relatedTarget) return;
      if (this.openItem.contains(event.relatedTarget)) return;
      this.closeMenu();
    });

    const backdrop = this.querySelector('[data-menu-backdrop]');
    if (backdrop) backdrop.addEventListener('click', () => this.closeMenu());

    /* A panel left open across the breakpoint would sit under a hidden nav. */
    this.onBreakpoint = () => {
      if (!DESKTOP.matches) this.closeMenu();
    };
    DESKTOP.addEventListener('change', this.onBreakpoint);
  }

  openMenu(item) {
    if (this.openItem === item) return;
    this.closeMenu();
    this.openItem = item;
    item.classList.add('is-open');
    this.classList.add('is-menu-open');
    this.syncMetrics();

    const panel = item.querySelector('.mega-header__panel');
    if (panel) {
      window.clearTimeout(panel.megaHideTimer);
      panel.hidden = false;
      nextFrame(() => {
        if (!panel.hidden) panel.classList.add('is-shown');
      });
    }
    const trigger = item.querySelector('.mega-header__link');
    if (trigger) trigger.setAttribute('aria-expanded', 'true');
  }

  closeMenu() {
    window.clearTimeout(this.closeTimer);
    if (!this.openItem) {
      this.classList.remove('is-menu-open');
      return;
    }
    const panel = this.openItem.querySelector('.mega-header__panel');
    if (panel) {
      panel.classList.remove('is-shown');
      hideAfter(panel, PANEL_FADE_OUT);
    }
    const trigger = this.openItem.querySelector('.mega-header__link');
    if (trigger) trigger.setAttribute('aria-expanded', 'false');
    this.openItem.classList.remove('is-open');
    this.openItem = null;
    this.classList.remove('is-menu-open');
  }

  /* --- side nav ---------------------------------------------------------- */

  initSideNav() {
    if (!this.sideNav || !this.burger) return;
    this.burger.addEventListener('click', () => this.setDrawer(!this.drawerOpen));
    this.sideNav.addEventListener('mega:sidenav', (event) => {
      this.burger.setAttribute('aria-expanded', String(event.detail.open));
    });
  }

  get drawerOpen() {
    return Boolean(this.sideNav && this.sideNav.isOpen);
  }

  setDrawer(open) {
    if (!this.sideNav) return;
    if (open) {
      this.closeMenu();
      this.sideNav.open(this.burger);
    } else {
      this.sideNav.close();
    }
  }

  /* --- theme editor -------------------------------------------------------
     Blocks that only render inside a closed panel or the side nav would have
     nothing to preview, so selecting one opens what contains it. */

  initEditor() {
    if (!window.Shopify || !window.Shopify.designMode) return;
    this.onBlockSelect = (event) => {
      if (!this.contains(event.target)) return;
      const item = event.target.closest('[data-mega-item]');
      if (item && DESKTOP.matches) {
        this.openMenu(item);
        return;
      }
      if (this.sideNav && this.sideNav.contains(event.target)) {
        this.setDrawer(true);
        const subnav = event.target.closest('.mega-side-nav__subnav');
        if (subnav) {
          const toggle = this.sideNav.querySelector(`[aria-controls="${subnav.id}"]`);
          if (toggle) this.sideNav.openSubnav(toggle);
        }
      }
    };
    this.onBlockDeselect = (event) => {
      if (!this.contains(event.target)) return;
      this.closeMenu();
    };
    document.addEventListener('shopify:block:select', this.onBlockSelect);
    document.addEventListener('shopify:block:deselect', this.onBlockDeselect);
  }
}

/* ---------------------------------------------------------------- side nav */

class MegaSideNav extends HTMLElement {
  connectedCallback() {
    this.panel = this.querySelector('.mega-side-nav__panel');
    this.main = this.querySelector('[data-side-nav-main]');
    this.isOpen = false;
    this.openSub = null;
    this.openSubTrigger = null;

    this.querySelectorAll('[data-side-nav-close]').forEach((el) => {
      el.addEventListener('click', () => this.close());
    });
    this.querySelectorAll('[data-side-nav-toggle]').forEach((button) => {
      button.addEventListener('click', () => this.openSubnav(button));
    });
    this.querySelectorAll('[data-side-nav-back]').forEach((button) => {
      button.addEventListener('click', () => this.closeSub(true));
    });
    /* The cart link inside the sheet opens the cart drawer; get out of its way
       first (cart.js also closes the header drawer through setDrawer). */
    this.querySelectorAll('[data-cart-open]').forEach((el) => {
      el.addEventListener('click', () => this.close());
    });

    /* Escape unwinds one level at a time; Tab stays inside the dialog. */
    this.onKeydown = (event) => {
      if (!this.isOpen) return;
      if (event.key === 'Escape') {
        if (this.openSub) this.closeSub(true);
        else this.close();
        return;
      }
      if (event.key === 'Tab') this.trapTab(event);
    };
  }

  disconnectedCallback() {
    if (this.isOpen) this.close();
    window.clearTimeout(this.resetTimer);
    window.clearTimeout(this.hideTimer);
  }

  trapTab(event) {
    const nodes = Array.from(this.panel.querySelectorAll(FOCUSABLE)).filter(isTabbable);
    if (!nodes.length) return;
    const first = nodes[0];
    const last = nodes[nodes.length - 1];
    const active = document.activeElement;
    if (!this.panel.contains(active)) {
      event.preventDefault();
      (event.shiftKey ? last : first).focus();
    } else if (event.shiftKey && (active === first || active === this.panel)) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus();
    }
  }

  /* --- drill-down -------------------------------------------------------- */

  openSubnav(button) {
    const id = button.getAttribute('aria-controls');
    const panel = id ? document.getElementById(id) : null;
    if (!panel || this.openSub === panel) return;
    this.closeSub(false);

    window.clearTimeout(panel.megaHideTimer);
    panel.hidden = false;
    this.openSub = panel;
    this.openSubTrigger = button;
    button.setAttribute('aria-expanded', 'true');
    this.classList.add('is-sub-open');
    /* The main list only slides aside, so it would stay in the Tab order
       between the head and the drill-down without this. */
    if (this.main) this.main.setAttribute('inert', '');

    /* Focus moves with the class: until `is-open` lands the panel is
       visibility: hidden, and focusing inside a hidden subtree is a no-op. */
    nextFrame(() => {
      if (panel.hidden) return;
      panel.classList.add('is-open');
      const back = panel.querySelector('[data-side-nav-back]');
      if (back) back.focus();
    });
  }

  /** @param {boolean} restoreFocus - true when a person asked (Back, Escape). */
  closeSub(restoreFocus) {
    this.classList.remove('is-sub-open');
    /* Hand the main list back before the early return — a stale `inert` would
       silently kill the whole menu's keyboard access. */
    if (this.main) this.main.removeAttribute('inert');
    if (!this.openSub) return;

    const panel = this.openSub;
    const trigger = this.openSubTrigger;
    this.openSub = null;
    this.openSubTrigger = null;
    panel.classList.remove('is-open');
    hideAfter(panel, SUBNAV_SLIDE);
    if (trigger) {
      trigger.setAttribute('aria-expanded', 'false');
      if (restoreFocus) trigger.focus();
    }
  }

  /* --- the sheet --------------------------------------------------------- */

  open(opener) {
    if (this.isOpen) return;
    this.isOpen = true;
    this.hidden = false;
    window.clearTimeout(this.resetTimer);
    window.clearTimeout(this.hideTimer);

    /* Only one modal traps Tab at a time: close the cart drawer first. */
    document.querySelectorAll('cart-drawer[data-open]').forEach((drawer) => {
      if (typeof drawer.close === 'function') drawer.close();
    });

    scrollLock().lock(this);
    /* Named by the caller: Safari does not focus a clicked <button>, so
       document.activeElement would be <body>. */
    this.opener = opener && opener.isConnected ? opener : document.activeElement;

    window.requestAnimationFrame(() => {
      if (!this.isOpen) return;
      this.classList.add('is-open');
      /* After the class: the sheet is visibility: hidden until then. */
      const close = this.querySelector('.mega-side-nav__close');
      if (close) close.focus();
    });

    document.addEventListener('keydown', this.onKeydown);
    this.dispatchEvent(new CustomEvent('mega:sidenav', { detail: { open: true } }));
  }

  close() {
    if (!this.isOpen) return;
    this.isOpen = false;
    this.classList.remove('is-open');
    document.removeEventListener('keydown', this.onKeydown);
    scrollLock().unlock(this);

    if (this.contains(document.activeElement) && this.opener && this.opener.isConnected) {
      this.opener.focus();
    }

    /* The drill-down resets only once the sheet has slid away; resetting now
       would snap the sub-panel away in full view. */
    window.clearTimeout(this.resetTimer);
    window.clearTimeout(this.hideTimer);
    const reset = () => this.closeSub(false);
    const hide = () => {
      this.hidden = true;
    };
    if (reducedMotion.matches) {
      reset();
      hide();
    } else {
      this.resetTimer = window.setTimeout(reset, DRAWER_SLIDE);
      this.hideTimer = window.setTimeout(hide, DRAWER_SLIDE);
    }
    this.dispatchEvent(new CustomEvent('mega:sidenav', { detail: { open: false } }));
  }
}

if (!customElements.get('mega-side-nav')) customElements.define('mega-side-nav', MegaSideNav);
if (!customElements.get('mega-header')) customElements.define('mega-header', MegaHeader);
